import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useFormStore } from '../../stores/formData';

// Utility function to format price from cents
const formatPrice = (priceCents) => {
  return (priceCents / 100).toFixed(2);
};

const capitalize = str => str ? str[0].toUpperCase() + str.slice(1) : str;

const SidebarOrderSummary = ({
  onBookNow,
  creatingOrder,
  orderCreated
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [feesExpanded, setFeesExpanded] = useState(false);
  
  const cart = useFormStore((state) => state.formData.cart);

  // Calculate totals from cart data - grouped by leg and traveler
  const { groupedItems, fees, discounts, totalAmount } = useMemo(() => {
    if (!cart || !cart.items || cart.items.length === 0) {
      return { groupedItems: [], fees: [], discounts: [], totalAmount: 0 };
    }

    const legGroups = {};
    const groupedFees = {}
    const groupedDiscounts = [];
    
    cart.items.forEach((item) => {
      if (!item.products || item.products.length === 0) return;

      const legIndex = item.leg?.leg_index || 1;
      const travelerName = item.traveler?.name || 'Unknown Traveler';
      const groupKey = `${legIndex}-${travelerName}`;

      // Initialize group if it doesn't exist
      if (!legGroups[groupKey]) {
        legGroups[groupKey] = {
          legIndex,
          travelerName,
          items: []
        };
      }

      const itemProducts = [];
      let itemSubtotal = 0;

      // Process each product in the item
      item.products.forEach(productWrapper => {
        const product = productWrapper.product;
        const productPrice = productWrapper.price_cents || 0;
        
        // Categorize products
        if (product.type === 'label') {
          // This is the main shipping product
          itemProducts.push({
            name: product.full_name || product.display_name || product.name,
            price: productPrice,
            type: 'shipping',
            description: product.description
          });
          itemSubtotal += productPrice;
        } else if (product.type === 'insurance') {
          // This is insurance
          itemProducts.push({
            name: `Insurance up to $${product.coverage_amount || '500'}`,
            price: productPrice,
            type: 'insurance'
          });
          itemSubtotal += productPrice;
        } else if (product.type === 'discount') {
          // This is a discount/coupon
          groupedDiscounts.push({
            name: product.coupon_code || 'Discount',
            price: productPrice,
            discountType: product.discount_type,
            category: product.category
          });
        } else if (product.type === 'fee') {
          // This is a fee - add to fees array
          const feeName = product.category ? `${product.category} fee` : 'Fee';
          const feeData = {
            name: capitalize(feeName),
            price: productPrice,
            category: product.category
          };
          if (!groupedFees[feeName]) {
            groupedFees[feeName] = feeData;
          } else {
            groupedFees[feeName].price += productPrice;
          }
        }
      });

      if (itemProducts.length > 0) {
        legGroups[groupKey].items.push({
          id: item.id,
          packaging: item.packaging?.name,
          desiredArrivalDate: item.desired_arrival_date,
          shipDate: item.ship_date,
          products: itemProducts,
          subtotal: itemSubtotal,
          totalPrice: item.price_cents
        });
      }
    });

    // Convert to array and sort by leg index
    const sortedGroups = Object.values(legGroups).sort((a, b) => a.legIndex - b.legIndex);

    return {
      groupedItems: sortedGroups,
      fees: Object.values(groupedFees),
      discounts: groupedDiscounts,
      totalAmount: cart.total_amount_cents || 0
    };
  }, [cart]);

  // Theme-based styling
  const containerClassName = clsx(
    'rounded-lg border p-4 space-y-4 sticky top-6',
    {
      'bg-slate-800 border-slate-700': isDark,
      'bg-white border-gray-200': !isDark
    }
  );

  const titleClassName = clsx(
    'text-xl font-bold',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const legTitleClassName = clsx(
    'text-sm font-semibold mb-2',
    {
      'text-slate-300': isDark,
      'text-gray-700': !isDark
    }
  );

  const travelerClassName = clsx(
    'text-xs mb-3',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  const productClassName = clsx(
    'flex justify-between items-center text-sm',
    {
      'text-slate-300': isDark,
      'text-gray-700': !isDark
    }
  );

  const insuranceClassName = clsx(
    'flex justify-between items-center text-xs ml-4',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  const feeClassName = clsx(
    'flex justify-between items-center text-sm',
    {
      'text-slate-400': isDark,
      'text-gray-600': !isDark
    }
  );

  const discountClassName = clsx(
    'flex justify-between items-center text-sm',
    {
      'text-green-400': isDark,
      'text-green-600': !isDark
    }
  );

  const totalClassName = clsx(
    'text-slate-900 rounded-lg p-0',
    {
      'bg-slate-700': isDark,
      'bg-white-900': !isDark
    }
  );

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className={containerClassName}>
        <h2 className={titleClassName}>Order summary</h2>
        <p className={clsx(
          'text-sm',
          {
            'text-slate-400': isDark,
            'text-gray-500': !isDark
          }
        )}>
          No items in cart
        </p>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <h2 className={titleClassName}>Order summary</h2>
      
      {/* Grouped Items Summary */}
      {groupedItems.map((group) => (
        <div key={`${group.legIndex}-${group.travelerName}`} className="space-y-3">
          <h3 className={legTitleClassName}>
            LEG {group.legIndex}
          </h3>
          
          <div className={travelerClassName}>
            Traveler: {group.travelerName}
          </div>
          
          {/* Items in this leg/traveler group */}
          <div className="space-y-3">
            {group.items.map((item) => (
              <div key={item.id} className="space-y-1">
                {/* Products */}
                {item.products.map((product, index) => (
                  <div key={index}>
                    {product.type === 'insurance' ? (
                      <div className={insuranceClassName}>
                        <span>{product.name}</span>
                        <span>${formatPrice(product.price)}</span>
                      </div>
                    ) : (
                      <div className={productClassName}>
                        <span>{product.name}</span>
                        <span>${formatPrice(product.price)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Fees Section */}
      {fees.length > 0 && (
        <div>
          <button
            onClick={() => setFeesExpanded(!feesExpanded)}
            className={clsx(
              'w-full flex items-center justify-between text-sm font-medium py-1',
              {
                'text-slate-300 hover:text-slate-200': isDark,
                'text-gray-700 hover:text-gray-900': !isDark
              }
            )}
          >
            <span>Fees</span>
            <div className="flex items-center gap-2">
              <span>${formatPrice(fees.reduce((sum, fee) => sum + fee.price, 0))}</span>
              {feesExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </button>
          
          {feesExpanded && (
            <div className="space-y-1 mt-2">
              {fees.map((fee, index) => (
                <div key={index} className={feeClassName}>
                  <span>{fee.name}</span>
                  <span>${formatPrice(fee.price)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Discounts Section */}
      {discounts.length > 0 && (
        <div className="space-y-1">
          {discounts.map((discount, index) => (
            <div key={index} className={discountClassName}>
              <span>Discount ({discount.name})</span>
              <span>-${formatPrice(Math.abs(discount.price))}</span>
            </div>
          ))}
        </div>
      )}

      {/* Total */}
      <div className={totalClassName}>
        <div className="flex justify-between items-center">
          <span className="text-lg font-bold">Total (USD)</span>
          <span className="text-xl font-bold text-green-500">
            ${formatPrice(totalAmount)}
          </span>
        </div>
      </div>

      {/* Book Now Button */}
      <button
        onClick={onBookNow}
        type={'button'}
        className={clsx(
          'w-full py-3 px-4 rounded-lg font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          {
            'bg-slate-700 hover:bg-slate-600 text-white focus:ring-slate-500': isDark,
            'bg-gray-900 hover:bg-gray-800 text-white focus:ring-gray-500': !isDark
          }
        )}
      >
        {creatingOrder && 'Processing Order...'}
        {orderCreated && 'Order Created Successfully!'}
        {!creatingOrder && !orderCreated && 'Book now'}
      </button>
    </div>
  );
};

SidebarOrderSummary.propTypes = {
  onBookNow: PropTypes.func,
  creatingOrder: PropTypes.bool,
  orderCreated: PropTypes.bool,
};

export default SidebarOrderSummary;